import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const RESULTADO_CONFIG = {
  ok:      { label: 'OK',      cls: 'bg-emerald-900/40 text-emerald-300 border-emerald-700/50' },
  nok:     { label: 'NOK',     cls: 'bg-red-900/40 text-red-300 border-red-700/50' },
  parcial: { label: 'Parcial', cls: 'bg-amber-900/40 text-amber-300 border-amber-700/50' },
};

// ─── Modal Nova Verificação ───────────────────────────────────────────────────
function ModalNovaVerificacao({ onFechar, onSalvo }) {
  const [busca, setBusca] = useState('');
  const [instrumentos, setInstrumentos] = useState([]);
  const [instrumentoSel, setInstrumentoSel] = useState(null);
  const [mostrarLista, setMostrarLista] = useState(false);
  const [data, setData] = useState(() => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now - offset).toISOString().slice(0, 16);
  });
  const [observacao, setObservacao] = useState('');
  const [pontos, setPontos] = useState([
    { aplicado: '', lido: '', empValor: '', empTipo: 'absoluto' },
  ]);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (busca.length < 2) { setInstrumentos([]); return; }
    const t = setTimeout(() => {
      api.get('/instruments', { params: { tag: busca, limit: 20 } })
        .then(r => setInstrumentos(r.data.data || r.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [busca]);

  function selecionarInstrumento(inst) {
    setInstrumentoSel(inst);
    setBusca(inst.tag);
    setMostrarLista(false);
  }

  function atualizarPonto(idx, campo, valor) {
    setPontos(prev => prev.map((p, i) => i === idx ? { ...p, [campo]: valor } : p));
  }

  function adicionarPonto() {
    setPontos(prev => [...prev, { aplicado: '', lido: '', empValor: '', empTipo: 'absoluto' }]);
  }

  function removerPonto(idx) {
    setPontos(prev => prev.filter((_, i) => i !== idx));
  }

  function calcularErro(p) {
    const a = parseFloat(p.aplicado);
    const l = parseFloat(p.lido);
    if (isNaN(a) || isNaN(l)) return null;
    return parseFloat((l - a).toFixed(6));
  }

  function pontoDentroEMP(p) {
    const erro = calcularErro(p);
    const emp = parseFloat(p.empValor);
    if (erro == null || isNaN(emp) || emp <= 0) return null;
    if (p.empTipo === 'percentual') {
      const empAbs = Math.abs(parseFloat(p.aplicado)) * (emp / 100);
      return Math.abs(erro) <= empAbs;
    }
    return Math.abs(erro) <= emp;
  }

  async function salvar() {
    setErro('');
    if (!instrumentoSel) return setErro('Selecione um instrumento.');
    if (!data) return setErro('Informe a data/hora.');
    const pontosValidos = pontos.filter(p => p.aplicado !== '' && p.lido !== '');
    if (pontosValidos.length === 0) return setErro('Preencha ao menos um ponto (aplicado e lido).');

    setSalvando(true);
    try {
      await api.post('/verificacoes', {
        instrumentId: instrumentoSel.id || instrumentoSel._id,
        data,
        observacao: observacao || undefined,
        pontos: pontosValidos.map(p => ({
          aplicado: parseFloat(p.aplicado),
          lido: parseFloat(p.lido),
          empValor: p.empValor !== '' ? parseFloat(p.empValor) : undefined,
          empTipo: p.empTipo || 'absoluto',
        })),
      });
      onSalvo();
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao salvar verificação.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="w-full max-w-xl bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--surface-4)]">
          <h2 className="text-sm font-semibold text-slate-100">Nova Verificação em Campo</h2>
          <button onClick={onFechar} className="text-slate-500 hover:text-slate-200 text-xl leading-none">×</button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4 flex-1">
          {/* Instrumento */}
          <div className="relative">
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Instrumento</label>
            <input
              type="text"
              value={busca}
              onChange={e => { setBusca(e.target.value); setInstrumentoSel(null); setMostrarLista(true); }}
              onFocus={() => setMostrarLista(true)}
              placeholder="Digite a TAG..."
              className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500"
            />
            {mostrarLista && instrumentos.length > 0 && (
              <div className="absolute z-10 top-full mt-1 w-full bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-lg shadow-xl max-h-48 overflow-y-auto">
                {instrumentos.map(inst => (
                  <button
                    key={inst.id || inst._id}
                    onClick={() => selecionarInstrumento(inst)}
                    className="w-full text-left px-3 py-2 hover:bg-[var(--surface-3)] text-sm"
                  >
                    <span className="font-mono text-slate-200">{inst.tag}</span>
                    <span className="text-slate-500 ml-2 text-xs">{inst.description}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Data */}
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Data / Hora</label>
            <input
              type="datetime-local"
              value={data}
              onChange={e => setData(e.target.value)}
              className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Pontos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Pontos de Verificação</label>
              <button
                onClick={adicionarPonto}
                className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
              >
                + Adicionar ponto
              </button>
            </div>
            <div className="space-y-2">
              {/* Cabeçalho */}
              <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 text-[10px] uppercase tracking-wider text-slate-600 px-1">
                <span>Aplicado</span>
                <span>Lido</span>
                <span>Erro</span>
                <span>EMP</span>
                <span />
              </div>
              {pontos.map((p, idx) => {
                const erro = calcularErro(p);
                const dentro = pontoDentroEMP(p);
                return (
                  <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-center">
                    <input
                      type="number"
                      value={p.aplicado}
                      onChange={e => atualizarPonto(idx, 'aplicado', e.target.value)}
                      placeholder="0"
                      className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1.5 text-xs text-slate-200 font-mono focus:outline-none focus:border-blue-500 w-full"
                    />
                    <input
                      type="number"
                      value={p.lido}
                      onChange={e => atualizarPonto(idx, 'lido', e.target.value)}
                      placeholder="0"
                      className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1.5 text-xs text-slate-200 font-mono focus:outline-none focus:border-blue-500 w-full"
                    />
                    <div className={`px-2 py-1.5 text-xs font-mono rounded text-center ${
                      dentro === true ? 'text-emerald-400 bg-emerald-900/20' :
                      dentro === false ? 'text-red-400 bg-red-900/20' :
                      'text-slate-500 bg-[var(--surface-1)]'
                    }`}>
                      {erro != null ? (erro >= 0 ? `+${erro}` : erro) : '—'}
                    </div>
                    <input
                      type="number"
                      value={p.empValor}
                      onChange={e => atualizarPonto(idx, 'empValor', e.target.value)}
                      placeholder="±EMP"
                      className="bg-[var(--surface-1)] border border-[var(--surface-4)] rounded px-2 py-1.5 text-xs text-slate-200 font-mono focus:outline-none focus:border-blue-500 w-full"
                    />
                    {pontos.length > 1 && (
                      <button onClick={() => removerPonto(idx)} className="text-slate-600 hover:text-red-400 text-sm leading-none px-1">×</button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Observação */}
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">Observação (opcional)</label>
            <textarea
              value={observacao}
              onChange={e => setObservacao(e.target.value)}
              rows={2}
              placeholder="Ex: Ajuste realizado antes da pesagem..."
              className="w-full bg-[var(--surface-1)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-600 resize-none focus:outline-none focus:border-blue-500"
            />
          </div>

          {erro && (
            <p className="text-xs text-red-400 bg-red-900/20 border border-red-800/40 rounded-lg px-3 py-2">{erro}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[var(--surface-4)] flex justify-end gap-2">
          <button onClick={onFechar} className="px-4 py-2 text-xs text-slate-400 hover:text-slate-200 transition-colors">
            Cancelar
          </button>
          <button
            onClick={salvar}
            disabled={salvando}
            className="px-4 py-2 text-xs font-semibold bg-blue-700 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {salvando ? 'Salvando...' : 'Registrar Verificação'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function VerificacoesPage() {
  const { user } = useAuth();
  const [verificacoes, setVerificacoes] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [carregando, setCarregando] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);
  const [toast, setToast] = useState(null);

  const [filtroInstrumento, setFiltroInstrumento] = useState('');
  const [filtroResultado, setFiltroResultado] = useState('');

  const [instrBusca, setInstrBusca] = useState('');
  const [instrLista, setInstrLista] = useState([]);
  const [instrDropdown, setInstrDropdown] = useState(false);

  const LIMITE = 15;

  function exibirToast(msg, tipo = 'ok') {
    setToast({ msg, tipo });
    setTimeout(() => setToast(null), 3500);
  }

  const buscar = useCallback(async () => {
    setCarregando(true);
    try {
      const params = { pagina, limite: LIMITE };
      if (filtroInstrumento) params.instrumentId = filtroInstrumento;
      if (filtroResultado) params.resultado = filtroResultado;
      const { data } = await api.get('/verificacoes', { params });
      setVerificacoes(data.dados);
      setTotal(data.total);
    } catch {
      exibirToast('Erro ao carregar verificações.', 'erro');
    } finally {
      setCarregando(false);
    }
  }, [pagina, filtroInstrumento, filtroResultado]);

  useEffect(() => { buscar(); }, [buscar]);

  useEffect(() => {
    if (instrBusca.length < 2) { setInstrLista([]); return; }
    const t = setTimeout(() => {
      api.get('/instruments', { params: { tag: instrBusca, limit: 20 } })
        .then(r => setInstrLista(r.data.data || r.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [instrBusca]);

  async function excluir(id) {
    if (!window.confirm('Excluir esta verificação?')) return;
    try {
      await api.delete(`/verificacoes/${id}`);
      exibirToast('Verificação excluída.');
      buscar();
    } catch {
      exibirToast('Erro ao excluir.', 'erro');
    }
  }

  const totalPaginas = Math.ceil(total / LIMITE);

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl text-sm font-medium shadow-lg border transition-all ${
          toast.tipo === 'erro'
            ? 'bg-red-900/90 text-red-200 border-red-700'
            : 'bg-emerald-900/90 text-emerald-200 border-emerald-700'
        }`}>
          {toast.msg}
        </div>
      )}

      {modalAberto && (
        <ModalNovaVerificacao
          onFechar={() => setModalAberto(false)}
          onSalvo={() => { setModalAberto(false); buscar(); exibirToast('Verificação registrada!'); }}
        />
      )}

      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">Verificações em Campo</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {total > 0 ? `${total} registro${total !== 1 ? 's' : ''}` : 'Nenhum registro'}
          </p>
        </div>
        {['supervisor', 'operador'].includes(user?.role) && (
          <button
            onClick={() => setModalAberto(true)}
            className="px-4 py-2 bg-blue-700 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            + Nova Verificação
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="flex gap-3 flex-wrap">
        {/* Filtro instrumento */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <input
            type="text"
            value={instrBusca}
            onChange={e => { setInstrBusca(e.target.value); setFiltroInstrumento(''); setInstrDropdown(true); }}
            onFocus={() => setInstrDropdown(true)}
            placeholder="Filtrar por TAG..."
            className="w-full bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500"
          />
          {instrDropdown && instrLista.length > 0 && (
            <div className="absolute z-10 top-full mt-1 w-full bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-lg shadow-xl max-h-48 overflow-y-auto">
              {instrLista.map(inst => (
                <button
                  key={inst.id || inst._id}
                  onClick={() => {
                    setInstrBusca(inst.tag);
                    setFiltroInstrumento(inst.id || inst._id);
                    setInstrDropdown(false);
                    setPagina(1);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-[var(--surface-3)] text-sm"
                >
                  <span className="font-mono text-slate-200">{inst.tag}</span>
                  <span className="text-slate-500 ml-2 text-xs">{inst.description}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <select
          value={filtroResultado}
          onChange={e => { setFiltroResultado(e.target.value); setPagina(1); }}
          className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
        >
          <option value="">Todos os resultados</option>
          <option value="ok">OK</option>
          <option value="nok">NOK</option>
          <option value="parcial">Parcial</option>
        </select>

        {(filtroInstrumento || filtroResultado) && (
          <button
            onClick={() => { setFiltroInstrumento(''); setFiltroResultado(''); setInstrBusca(''); setPagina(1); }}
            className="text-xs text-slate-500 hover:text-slate-300 transition-colors px-2"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {/* Tabela */}
      <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-xl overflow-hidden">
        {carregando ? (
          <div className="flex items-center justify-center h-32 text-slate-500 text-sm">Carregando...</div>
        ) : verificacoes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-slate-500 gap-2">
            <span className="text-3xl opacity-40">🔍</span>
            <p className="text-sm">Nenhuma verificação encontrada.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--surface-4)] text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 text-left">Data / Hora</th>
                <th className="px-4 py-3 text-left">Instrumento</th>
                <th className="px-4 py-3 text-left">Setor</th>
                <th className="px-4 py-3 text-left">Pontos</th>
                <th className="px-4 py-3 text-left">Resultado</th>
                <th className="px-4 py-3 text-left">Responsável</th>
                <th className="px-4 py-3 text-left">Observação</th>
                {['supervisor'].includes(user?.role) && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {verificacoes.map(v => {
                const res = RESULTADO_CONFIG[v.resultado] || RESULTADO_CONFIG.ok;
                const nok = v.pontos.filter(p => !p.aprovado).length;
                return (
                  <tr key={v.id} className="border-b border-[var(--surface-4)]/50 last:border-0 hover:bg-[var(--surface-3)]/30 transition-colors">
                    <td className="px-4 py-3 text-slate-300 font-mono text-xs whitespace-nowrap">
                      {new Date(v.data).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-slate-200 text-xs">{v.instrument?.tag}</span>
                      <p className="text-[11px] text-slate-500 mt-0.5 truncate max-w-[160px]">{v.instrument?.description}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">{v.instrument?.sector || '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-400">
                      {v.pontos.length} ponto{v.pontos.length !== 1 ? 's' : ''}
                      {nok > 0 && <span className="text-red-400 ml-1">({nok} fora)</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${res.cls}`}>
                        {res.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">{v.responsavelNome}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 max-w-[160px] truncate">{v.observacao || '—'}</td>
                    {['supervisor'].includes(user?.role) && (
                      <td className="px-4 py-3">
                        <button
                          onClick={() => excluir(v.id)}
                          className="text-slate-600 hover:text-red-400 text-xs transition-colors"
                        >
                          Excluir
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginação */}
      {totalPaginas > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPagina(p => Math.max(1, p - 1))}
            disabled={pagina === 1}
            className="px-3 py-1.5 text-xs bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-lg text-slate-400 hover:text-slate-200 disabled:opacity-40 transition-colors"
          >
            ← Anterior
          </button>
          <span className="text-xs text-slate-500">{pagina} / {totalPaginas}</span>
          <button
            onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}
            disabled={pagina === totalPaginas}
            className="px-3 py-1.5 text-xs bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-lg text-slate-400 hover:text-slate-200 disabled:opacity-40 transition-colors"
          >
            Próxima →
          </button>
        </div>
      )}
    </div>
  );
}
